import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Sidebar } from "@/components/Sidebar";
import { SudokuGame } from "@/components/SudokuGame";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";

const SudokuPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isDarkMode = useIsDarkMode();

  const handleMenuClick = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-gray-100 via-white to-gray-100'
    }`}>

      {/* Sidebar */}
      <Sidebar 
        isOpen={isSidebarOpen} 
        onToggle={handleMenuClick}
        onAuth={() => navigate('/auth')}
        user={null}
        onNewChat={() => navigate('/')}
        isDarkMode={isDarkMode}
      />

      {/* Main Content */}
      <main
        className={`pt-20 pb-8 px-0 sm:px-4 transition-colors duration-300 ${
          isDarkMode ? "bg-[#1c1c1c]" : "bg-[#f8fafc]"
        }`}
      >
        <div
          className={`container mx-auto px-0 sm:px-4 transition-colors duration-300 ${
            isDarkMode ? "bg-[#1c1c1c]" : "bg-white"
          }`}
        >

          {/* Sudoku Game Component */}
          <div className="flex justify-center">
            <SudokuGame />
          </div>

          {/* Game Instructions */}
          <div
            className={`mt-12 max-w-4xl mx-auto sm:px-8 px-4 py-6 rounded-xl shadow-lg transition-colors ${
              isDarkMode
                ? 'bg-[#232323] border border-white/5 text-gray-200'
                : 'bg-white/80 border border-slate-200 text-slate-700'
            }`}
          >
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              How to play Sudoku
            </h2>
            <p className="text-base leading-relaxed mb-6">
              Fill the 9×9 grid so that every row, column, and 3×3 block contains the digits 1 through 9 exactly once. Each puzzle starts with a few numbers already placed. Use logic to determine where the remaining digits belong without guessing.
            </p>
            <div
              className={`rounded-lg py-5 sm:px-5 px-3 mb-8 ${
                isDarkMode
                  ? 'bg-[#2e2e2e] border border-white/5 text-gray-200'
                  : 'bg-slate-50 border border-slate-200 text-slate-700'
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-3 ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                A move breaks the rules when:
              </h3>
              <ul className="space-y-2 text-sm leading-relaxed">
                <li>Any row repeats the same number from 1 to 9.</li>
                <li>Any column repeats the same number from 1 to 9.</li>
                <li>Any 3×3 section repeats the same number from 1 to 9.</li>
              </ul>
            </div>
            <h3
              className={`text-xl font-semibold mb-3 ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Sudoku tips
            </h3>
            <div className="space-y-4 text-sm leading-relaxed">
              <div>
                <p className="font-medium mb-1 text-base">Tip 1</p>
                <p>
                  Scan rows, columns, or blocks that already have five or more digits. List the missing numbers and test each open cell to see where a single value can logically fit based on the existing numbers around it.
                </p>
              </div>
              <div>
                <p className="font-medium mb-1 text-base">Tip 2</p>
                <p>
                  Picture the grid as three tall columns and three wide rows of 3×3 blocks. When two blocks in a column already contain the same digit, the final block in that column must also include that digit—narrow down which of the remaining cells can legally hold it.
                </p>
              </div>
            </div>
          </div>

          
        </div>
      </main>

      {/* Background overlay when sidebar is open */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default SudokuPage;
